import React, { useState, useEffect } from 'react';
import { Role } from '../../constants/roles';
import styles from './VideoPlayer.module.scss';
import { getUserDisplayNameWithDeleted, isUserDeleted } from '../../utils/userPresence';
import { DeletedUserAvatarShell } from '../UserPresence/DeletedUserAvatarShell';

interface SeekBarProps {
  seekbarRef: React.RefObject<HTMLDivElement>;
  currentTime: number;
  duration: number;
  comments: Array<{
    id: number;
    startFrame: number;
    endFrame: number;
    text: string;
    user?: {
      firstName: string;
      lastName: string;
      email?: string;
      role?: string;
      thumbnailUrl?: string;
      isDeleted?: boolean;
    };
    isResolved?: boolean;
  }>;
  fps?: number;
  selectedCommentId?: number | null;
  onSeekbarClick: (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => void;
  onSeekbarDrag: (e: React.MouseEvent<HTMLDivElement> | { clientX: number }) => void;
  onCommentClick?: (commentId: number) => void;
  showResolved?: boolean;
  children?: React.ReactNode;
  /** Aralık döngüsü açıkken ana çubukta aralık dışı / aralık renkleri */
  isRangeLooping?: boolean;
  rangeLoopStart?: number;
  rangeLoopEnd?: number;
}

const SeekBar: React.FC<SeekBarProps> = ({
  seekbarRef,
  currentTime,
  duration,
  comments,
  fps = 30,
  selectedCommentId,
  onSeekbarClick,
  onSeekbarDrag,
  onCommentClick,
  showResolved,
  children,
  isRangeLooping = false,
  rangeLoopStart = 0,
  rangeLoopEnd = 0,
}) => {
  const rangeLoopBarInactive = '#7EC4C040';

  const useRangeLoopSeekStyle =
    isRangeLooping && duration > 0 && rangeLoopEnd > rangeLoopStart;

  let progressLeftPct = 0;
  let progressWidthPct = 0;
  let progressBackground = '#7EC4C0BF';

  if (duration > 0) {
    if (useRangeLoopSeekStyle) {
      const rs = rangeLoopStart;
      const re = rangeLoopEnd;
      const capped = Math.min(currentTime, re);
      const fillDuration = Math.max(0, capped - rs);
      progressLeftPct = (rs / duration) * 100;
      progressWidthPct = (fillDuration / duration) * 100;
      progressBackground = '#FFCA1C';
    } else {
      progressWidthPct = (currentTime / duration) * 100;
    }
  }

  /* Loop’ta arka plan tek renk; sarı yalnızca üstteki dolgu ile range start→current büyür */
  const seekBarBackground = useRangeLoopSeekStyle
    ? rangeLoopBarInactive
    : '#7ec4c040';

  // Mouse sürükleme state'i
  const [isDragging, setIsDragging] = useState(false);

  // Mouse sürükleme başladığında
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    onSeekbarDrag(e);
  };

  // Mouse sürükleme bittiğinde
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Document seviyesinde mouse hareketi dinle
  useEffect(() => {
    if (isDragging) {
      const handleDocumentMouseMove = (e: MouseEvent) => {
        onSeekbarDrag({ clientX: e.clientX } as any);
      };

      const handleDocumentMouseUp = () => {
        setIsDragging(false);
      };

      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp);
      };
    }
  }, [isDragging, onSeekbarDrag]);

  // Resolved pinleri filtrele
  const visibleComments = (showResolved ? comments : comments.filter(c => !c.isResolved));

  return (
    <div
      className={styles.seekBarHost}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      <div
        ref={seekbarRef}
        className={styles.seekBarRailWrap}
        onClick={onSeekbarClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          const rect = seekbarRef.current?.getBoundingClientRect();
          if (rect) {
            onSeekbarDrag({ clientX: touch.clientX } as any);
          }
        }}
        onTouchMove={(e) => {
          e.preventDefault();
          const touch = e.touches[0];
          const rect = seekbarRef.current?.getBoundingClientRect();
          if (rect) {
            onSeekbarDrag({ clientX: touch.clientX } as any);
          }
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          const touch = e.changedTouches[0];
          const rect = seekbarRef.current?.getBoundingClientRect();
          if (rect) {
            onSeekbarClick({ clientX: touch.clientX } as any);
          }
        }}
      >
        <div
          className={styles.seekBarTrackClip}
          style={{ background: seekBarBackground }}
          aria-hidden
        >
          <div
            style={{
              position: 'absolute',
              left: `${progressLeftPct}%`,
              top: 0,
              height: 8,
              width: `${progressWidthPct}%`,
              background: progressBackground,
              borderRadius: 4,
            }}
          />
        </div>
      
      {/* Comment pinleri */}
      {visibleComments.map((comment) => {
        const commentStartTime = comment.startFrame / fps;
        const commentEndTime = comment.endFrame / fps;
        const commentStartPosition = (commentStartTime / duration) * 100;
        const commentEndPosition = (commentEndTime / duration) * 100;
        
        // Range farkı 1'den büyükse çizgi göster
        const showRange = (comment.endFrame - comment.startFrame) > 1;
        const isSelected = selectedCommentId === comment.id;
        
        const thumb = comment.user?.thumbnailUrl;
        const initial =
          (comment.user?.firstName?.trim()?.[0] ||
            comment.user?.email?.trim()?.[0] ||
            'U').toUpperCase();
        const authorDeleted = isUserDeleted(comment.user);
        const isGuest = !authorDeleted && comment.user?.role === Role.Guest;
        const authorTitle = getUserDisplayNameWithDeleted(comment.user ?? null, null);

        return (
          <div key={comment.id}>
            <button
              type="button"
              className={`${styles.commentSeekAvatar} ${isSelected ? styles.commentSeekAvatarSelected : ''} ${isGuest && !thumb ? styles.commentSeekAvatarGuest : ''}`}
              style={{ left: `${commentStartPosition}%` }}
              onClick={(e) => {
                e.stopPropagation();
                onCommentClick?.(comment.id);
              }}
              title={`${authorTitle}: ${comment.text.substring(0, 50)}${comment.text.length > 50 ? '...' : ''}`}
              aria-label={`Yorum: ${authorTitle}`}
            >
              <DeletedUserAvatarShell deleted={authorDeleted}>
                {thumb ? (
                  <img src={thumb} alt="" width={14} height={14} draggable={false} />
                ) : (
                  <span className={styles.commentSeekAvatarInitial}>{initial}</span>
                )}
              </DeletedUserAvatarShell>
            </button>

            {showRange && (
              <>
                <div
                  className={styles.commentSeekRange}
                  style={{
                    left: `${commentStartPosition}%`,
                    width: `${Math.max(commentEndPosition - commentStartPosition, 0.1)}%`,
                  }}
                />
                <div
                  className={styles.commentSeekRangeEnd}
                  style={{ left: `${commentEndPosition}%` }}
                  aria-hidden
                />
              </>
            )}
          </div>
        );
      })}
      </div>
      {children}
    </div>
  );
};

export default SeekBar; 